/**
 * ProductCapacityDrawer — right-side panel showing per-day capacity
 * for a single inventory-tracked product. Allows override edits (PUT)
 * and restock adjustments (POST adjust).
 *
 * SA-814: on the shared Drawer primitive, resizable. An override saves on
 * blur; closing by Escape or the overlay can unmount the box without a blur,
 * so a close saves every draft that differs from the stored override.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { cn } from '@/utils'
import { CalendarDays, RefreshCw } from 'lucide-react'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, Spinner } from '@/components/ui'
import { useDrawerWidth } from '@/hooks/useDrawerWidth'
import { apiClient } from '@/services/apiClient'

/** The table needs 484px; 512 leaves room for a classic scrollbar (SA-814). */
const PRODUCT_CAPACITY_WIDTH = { default: 512, min: 448 }

interface CapacityDay {
  date: string
  effective_capacity: number
  capacity_override: number | null
  reserved: number
  committed: number
  remaining: number
}

interface ProductCapacityDrawerProps {
  productId: string
  open: boolean
  onClose: () => void
}

export function ProductCapacityDrawer({ productId, open, onClose }: ProductCapacityDrawerProps) {
  const [days, setDays] = useState<CapacityDay[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [overrideDraft, setOverrideDraft] = useState<Record<string, string>>({})
  const [restockDate, setRestockDate] = useState<string | null>(null)
  const [restockDelta, setRestockDelta] = useState('')
  const [savingDate, setSavingDate] = useState<string | null>(null)
  const [restocking, setRestocking] = useState(false)
  // The last value sent per date: a blur's save may still be in flight, with
  // `days` not yet refetched, when the X closes the drawer.
  const sent = useRef<Record<string, number | null>>({})

  const today = new Date().toISOString().slice(0, 10)
  const lookahead = 30
  const toDate = new Date(Date.now() + lookahead * 86400_000).toISOString().slice(0, 10)

  const fetchCapacity = useCallback(async () => {
    if (!productId) return
    setIsLoading(true)
    try {
      const res = await apiClient.fetch(
        `/api/commerce/products/${productId}/capacity?from=${today}&to=${toDate}`,
      )
      if (res.ok) {
        const data = await res.json()
        setDays(data.items || [])
      }
    } catch {
      // silent
    } finally {
      setIsLoading(false)
    }
  }, [productId, today, toDate])

  useEffect(() => {
    if (open) {
      fetchCapacity()
      setOverrideDraft({})
      sent.current = {}
      setRestockDate(null)
    }
  }, [open, fetchCapacity])

  const saveOverride = (date: string, value: number | null) => {
    sent.current[date] = value
    return apiClient.fetch(`/api/commerce/products/${productId}/capacity/${date}`, {
      method: 'PUT',
      body: JSON.stringify({ capacity_override: value }),
    })
  }

  const draftValue = (raw: string) => (raw === '' ? null : parseInt(raw, 10))

  const handleOverrideBlur = async (date: string) => {
    const raw = overrideDraft[date]
    if (raw === undefined) return // unchanged
    setSavingDate(date)
    try {
      await saveOverride(date, draftValue(raw))
      await fetchCapacity()
    } finally {
      setSavingDate(null)
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (next) return
    const unsaved = Object.entries(overrideDraft).filter(([date, raw]) => {
      const day = days.find(d => d.date === date)
      if (day === undefined) return false
      const saved = date in sent.current ? sent.current[date] : day.capacity_override
      return draftValue(raw) !== saved
    })
    onClose()
    for (const [date, raw] of unsaved) void saveOverride(date, draftValue(raw))
  }

  const handleRestock = async () => {
    if (!restockDate || !restockDelta) return
    const delta = parseInt(restockDelta, 10)
    if (isNaN(delta) || delta <= 0) return
    setRestocking(true)
    try {
      await apiClient.fetch(`/api/commerce/products/${productId}/inventory/adjust`, {
        method: 'POST',
        body: JSON.stringify({ date: restockDate, delta, reason: 'manual restock' }),
      })
      setRestockDate(null)
      setRestockDelta('')
      await fetchCapacity()
    } finally {
      setRestocking(false)
    }
  }

  const resize = useDrawerWidth('product-capacity', PRODUCT_CAPACITY_WIDTH, 'Resize capacity panel')

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent resize={resize}>
        <DrawerHeader>
          <div className="flex items-center gap-2 text-foreground">
            <CalendarDays className="w-5 h-5 text-primary" />
            <DrawerTitle>Daily Capacity</DrawerTitle>
          </div>
        </DrawerHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : days.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground text-sm">No capacity data</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2 text-right">Rem.</th>
                  <th className="px-4 py-2 text-right">Res.</th>
                  <th className="px-4 py-2 text-right">Com.</th>
                  <th className="px-4 py-2">Override</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {days.map(day => (
                  <tr key={day.date} className="hover:bg-muted">
                    <td className="px-4 py-2 font-mono text-xs text-foreground">
                      {day.date}
                    </td>
                    <td className={cn(
                      'px-4 py-2 text-right font-semibold',
                      day.remaining <= 0
                        ? 'text-destructive'
                        : day.remaining <= 3
                          ? 'text-warning-strong'
                          : 'text-success',
                    )}>
                      {day.remaining}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{day.reserved}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{day.committed}</td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min={0}
                        placeholder={String(day.effective_capacity)}
                        value={overrideDraft[day.date] ?? (day.capacity_override !== null ? String(day.capacity_override) : '')}
                        onChange={e => setOverrideDraft(d => ({ ...d, [day.date]: e.target.value }))}
                        onBlur={() => handleOverrideBlur(day.date)}
                        disabled={savingDate === day.date}
                        className="w-16 px-2 py-0.5 text-xs rounded-sm border border-border bg-background disabled:opacity-50"
                        aria-label={`Capacity override for ${day.date}`}
                      />
                    </td>
                    <td className="px-4 py-2">
                      {savingDate === day.date ? (
                        <Spinner className="w-3 h-3" />
                      ) : (
                        <button
                          onClick={() => {
                            setRestockDate(day.date)
                            setRestockDelta('')
                          }}
                          className="text-xs text-primary hover:text-primary/80 whitespace-nowrap"
                          title="Add restock"
                        >
                          + Restock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Restock panel (shown when a date is selected) */}
        {restockDate && (
          <div className="border-t border-border px-5 py-4 bg-muted space-y-3">
            <p className="text-xs font-semibold text-foreground">
              Restock for <span className="font-mono">{restockDate}</span>
            </p>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                min={1}
                placeholder="Units to add"
                value={restockDelta}
                onChange={e => setRestockDelta(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
                aria-label="Restock amount"
              />
              <button
                onClick={handleRestock}
                disabled={restocking || !restockDelta}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {restocking ? <Spinner className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Add
              </button>
              <button
                onClick={() => setRestockDate(null)}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  )
}
